import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";

import ColorUtil from "@plannel/pages/utils/color-util";
import { FS } from "@plannel/utils/font-util";

/**
 * 아바타가 대표로 쓰는 이름.
 *
 * <p>계정을 앞세운다 — 담당자 선택 목록과 같은 기준이다. 이 제품은 disp_name 이 비어 있거나
 * 자리 이름이 들어 있는 계정이 흔해서, 그쪽을 기준으로 삼으면 같은 사람이 화면마다 달라 보인다.
 */
const nameOf = (assignee) => assignee?.username || assignee?.dispName || "";

/** 이름에서 아바타에 넣을 글자. 한글은 한 자, 로마자는 머리글자 두 자가 읽힌다 */
const initialsOf = (name) => {
  const text = String(name ?? "").trim();
  if (!text) {
    return "?";
  }
  if (/^[\x20-\x7F]+$/.test(text)) {
    const words = text.split(/\s+/).filter(Boolean);
    return (words.length > 1 ? words[0][0] + words[words.length - 1][0] : text.slice(0, 2)).toUpperCase();
  }
  return text.slice(0, 1);
};

/**
 * 같은 사람은 어디서나 같은 색이 되게 이름에서 색을 고른다.
 *
 * <p>임의로 돌리면 목록을 다시 그릴 때마다 색이 바뀌어서 색이 아무것도 뜻하지 않는다.
 * 팔레트는 차트와 같은 것을 쓴다(사용자가 고른 그 팔레트) — 여기서 색을 새로 정하지 않는다.
 */
const colorOf = (name) => {
  const text = String(name ?? "");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) % 997;
  }
  return ColorUtil.colorSet(hash % 12, 500);
};

/**
 * 배정된 사람들을 아바타로 겹쳐 보여 준다.
 *
 * <p>카드에서 "이 회의가 나와 상관있나" 를 알려 주는 단서는 이것뿐이다 — 제목·태그·상태로는
 * 알 수 없다. 그래서 목록 조회에서도 담당자를 같이 싣는다.
 *
 * @param {object[]} assignees {@code [{ userId, dispName, deptName, readFlg }]}
 * @param {number} [max] 이보다 많으면 나머지는 +N 으로 접는다
 */
const AssigneeAvatars = ({ assignees, max = 4, size = 24 }) => {
  if (!assignees?.length) {
    return null;
  }
  const shown = assignees.slice(0, max);
  const hidden = assignees.slice(max);

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {shown.map((assignee, index) => (
        <Tooltip
          key={assignee.userId}
          // 담당자 선택 목록과 같은 순서로 읽는다 — 계정이 먼저, 자리가 뒤.
          // 아바타 글자 두 개만으로는 누구인지 알 수 없으므로 여기서 전부 펼친다
          title={[
            nameOf(assignee),
            assignee.postnNames,
            assignee.deptName,
            assignee.dispName === assignee.username ? null : assignee.dispName
          ]
            .filter(Boolean)
            .join(" · ")}
        >
          <Avatar
            sx={{
              width: size,
              height: size,
              fontSize: FS.CAPTION,
              fontWeight: 700,
              bgcolor: colorOf(nameOf(assignee)),
              // 겹쳐 놓으면 경계가 사라진다 — 배경색 테두리로 서로를 떼어 놓는다
              border: 2,
              borderColor: "background.paper",
              ml: index === 0 ? 0 : `-${Math.round(size / 3)}px`
            }}
          >
            {initialsOf(nameOf(assignee))}
          </Avatar>
        </Tooltip>
      ))}
      {hidden.length > 0 && (
        <Tooltip title={hidden.map(nameOf).join(", ")}>
          <Avatar
            sx={{
              width: size,
              height: size,
              fontSize: FS.CAPTION,
              fontWeight: 700,
              bgcolor: "action.selected",
              color: "text.secondary",
              border: 2,
              borderColor: "background.paper",
              ml: `-${Math.round(size / 3)}px`
            }}
          >
            {`+${hidden.length}`}
          </Avatar>
        </Tooltip>
      )}
    </Box>
  );
};

export default AssigneeAvatars;
